import cx from 'classnames';
import type { VoidFunctionComponent } from 'react';

import { plural } from '../../../../../../../shared/helpers/plural';
import { isCustomLicense, type LicenseGroup } from '../../../../../../../shared/licenses';
import { useDresser } from '../../../../../../contexts/DrawerContext';
import Badge from '../../../../../generic/Badge/Badge';
import Button from '../../../../../generic/Button/Button';
import { BUTTON_SIZE, BUTTON_TYPE } from '../../../../../generic/Button/button-constants';
import Flex from '../../../../../generic/Flex';
import { icons } from '../../../../../generic/Icon/icons';
import List from '../../../../../generic/List';
import LicenseDrawer from '../../../../../licenses/LicenseDrawer';
import type { Dependency } from '../../../../../../../shared/dependenciesV2';

import CustomLicenseName from './CustomLicenseName';
import { LicenseActionButtons } from './LicenseActionButtons';
import { PolicyStatusBadge } from './PolicyStatusBadge';
import type { LicenseIssueTypeMap, LicenseTabScope } from './types';

import styles from './DependencyLicenses.module.less';

type Props = {
  licenseGroups: LicenseGroup[];
  licenseIssueTypeMap: LicenseIssueTypeMap;
  concludedLicenses: string[];
  licenseType: 'declared' | 'discovered';
  scope: LicenseTabScope;
  isLoading: boolean;
  dependency: Pick<Dependency, 'title' | 'version' | 'locator'>;
  canEditLicenses: boolean;
  onAddLicense: (licenseGroup?: LicenseGroup) => void;
  isLicenseConcludedEnabled: boolean;
  isLicenseScanningEnabled: boolean;
};

type LicenseGroupTableProps = Pick<Props, 'licenseIssueTypeMap' | 'concludedLicenses' | 'scope' | 'dependency' | 'isLicenseConcludedEnabled' | 'isLicenseScanningEnabled'> & {
  licenseGroup: LicenseGroup;
  canEditLicenses: boolean;
  onAddLicense: (licenseGroup?: LicenseGroup) => void;
};

const LoadingState: VoidFunctionComponent<Pick<Props, 'scope'>> = ({ scope }) => {
  return (
    <List>
      <List.Header>
        <div className={cx(styles.licenseTable, styles.header, { [styles.global]: scope.type === 'global' })}>
          <List.HeaderText>License</List.HeaderText>
          <List.HeaderText>Policy Status</List.HeaderText>
          {scope.type === 'global' && <List.HeaderText>Concluded In</List.HeaderText>}
          <List.HeaderText>Actions</List.HeaderText>
        </div>
      </List.Header>
      <List.Body>
        <List.RowContainer>
          <List.RowLoading />
        </List.RowContainer>
      </List.Body>
    </List>
  );
};

const LicenseGroupTable: VoidFunctionComponent<LicenseGroupTableProps> = ({
  licenseGroup,
  licenseIssueTypeMap,
  concludedLicenses,
  scope,
  dependency,
  canEditLicenses,
  onAddLicense,
  isLicenseConcludedEnabled,
  isLicenseScanningEnabled,
}) => {
  const { pushDrawer } = useDresser();

  return (
    <>
      {licenseGroup.licenses.length > 1 && (
        <span className={styles.licenseGroupSignature}> {licenseGroup.signature}</span>
      )}
      <List>
        <List.Header>
          <div className={cx(styles.licenseTable, styles.header, { [styles.global]: scope.type === 'global' })}>
            <List.HeaderText>License</List.HeaderText>
            <List.HeaderText>Policy Status</List.HeaderText>
            {scope.type === 'global' && <List.HeaderText>Concluded In</List.HeaderText>}
            <List.HeaderText>Actions</List.HeaderText>
          </div>
        </List.Header>
        <List.Body>
          {licenseGroup.licenses.length > 0 &&
            licenseGroup.licenses.map((license) => (
              <List.RowContainer key={license.licenseId}>
                <List.Row isInteractive={false} hasTightVerticalPadding>
                  <div className={cx(styles.licenseTable, styles.row, { [styles.global]: scope.type === 'global' })}>
                    {/* License Column */}
                    <Flex justify="flex-start" gap="12px">
                      {isCustomLicense(license.licenseId) ? (
                        <CustomLicenseName
                          licenseId={license.licenseId}
                          dependencyLocator={dependency.locator}
                          revisionLicenseId={license.revisionLicenseId}
                        />
                      ) : (
                        license.licenseId
                      )}
                      {isLicenseConcludedEnabled && concludedLicenses.includes(license.licenseId) && (
                        <Badge color="white" size="micro">
                          <div className={styles.concludedLicenseBadgeText}>Concluded</div>
                        </Badge>
                      )}
                    </Flex>
                    {/* Policy Status Column */}
                    <div>
                      <PolicyStatusBadge
                        issueType={licenseIssueTypeMap[license.licenseId]?.type}
                        isIgnored={licenseIssueTypeMap[license.licenseId]?.isIgnored}
                        isScanned={isLicenseScanningEnabled}
                      />
                    </div>
                    {/* Optional "Concluded In" Column */}
                    {scope.type === 'global' &&
                      (scope.concludedLicenseCountMap[license.licenseId] ? (
                        <div>
                          {scope.concludedLicenseCountMap[license.licenseId]}{' '}
                          {plural(scope.concludedLicenseCountMap[license.licenseId] || 0, 'project')}
                        </div>
                      ) : (
                        // Empty div to keep the column aligned
                        <div />
                      ))}
                    {/* Action Buttons Column */}
                    <div>
                      <LicenseActionButtons
                        licenseSignature={license.licenseId}
                        revisionLicenseId={license.revisionLicenseId}
                        isConcluded={concludedLicenses.includes(license.licenseId)}
                        scope={scope}
                        canEditLicenses={canEditLicenses}
                        isLicenseConcludedEnabled={isLicenseConcludedEnabled}
                        onView={() => {
                          pushDrawer(
                            <LicenseDrawer
                              scope="dependency"
                              revisionLicenseId={license.revisionLicenseId}
                              dependency={dependency}
                            />,
                          );
                        }}
                      />
                    </div>
                  </div>
                </List.Row>
              </List.RowContainer>
            ))}
          {canEditLicenses && (
            <List.Row isInteractive={false} hasTightVerticalPadding>
              <div className={styles.addLicenseButton}>
                <Button
                  size={BUTTON_SIZE.SMALL}
                  type={BUTTON_TYPE.TEXT}
                  icon={icons.plusCircleO}
                  onClick={() => onAddLicense(licenseGroup)}
                >
                  Add a license
                </Button>
              </div>
            </List.Row>
          )}
        </List.Body>
      </List>
    </>
  );
};

const DependencyLicensesTable: VoidFunctionComponent<Props> = ({
  licenseGroups,
  licenseIssueTypeMap,
  concludedLicenses,
  licenseType,
  scope,
  isLoading,
  dependency,
  canEditLicenses,
  onAddLicense,
  isLicenseConcludedEnabled,
  isLicenseScanningEnabled,
}) => {
  if (isLoading) {
    return <LoadingState scope={scope} />;
  }

  if (licenseGroups.length === 0) {
    return <div className={styles.emptyState}>No {licenseType} licenses found</div>;
  }

  return (
    <>
      {licenseGroups.map((licenseGroup) => (
        <LicenseGroupTable
          dependency={dependency}
          licenseGroup={licenseGroup}
          licenseIssueTypeMap={licenseIssueTypeMap}
          concludedLicenses={concludedLicenses}
          scope={scope}
          canEditLicenses={canEditLicenses}
          onAddLicense={onAddLicense}
          isLicenseConcludedEnabled={isLicenseConcludedEnabled}
          isLicenseScanningEnabled={isLicenseScanningEnabled}
        />
      ))}
    </>
  );
};

export default DependencyLicensesTable;
